import React from 'react';
import { CheckCircle } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';

/**
 * Pure presentational render of the post-purchase account-claim card.
 *
 * IMPORTANT: This component is FORM ONLY. It does not own validation,
 * API calls, or the claim handler. The parent (EventPage) still owns:
 *   - handleClaimAccount (validation + POST /auth/claim-account)
 *   - claimForm state + setClaimForm setter
 *   - claiming flag
 *   - navigate
 *
 * Props:
 *   claimForm     — { screen_name, password }
 *   setClaimForm  — setter from useState (not wrapped)
 *   onClaim       — () => void, invokes parent's handleClaimAccount
 *   claiming      — bool, disables the button while in-flight
 *   purchaseEmail — email the pass was saved to
 *   ticketId      — ticket id for the "Skip" navigation target
 *   navigate      — react-router navigate function (from the parent scope)
 *   isManual      — bool, true if event.release_method === 'manual'.
 *                   When true the header reflects the actual access state
 *                   ("Request Submitted") instead of implying confirmed
 *                   access ("Pass Secured!"). Iter125 copy alignment.
 */
export default function ClaimAccountCard({
  claimForm,
  setClaimForm,
  onClaim,
  claiming,
  purchaseEmail,
  ticketId,
  navigate,
  isManual = false,
}) {
  return (
    <div className="bg-[#0A0A0A] border border-[#D4AF37]/20 rounded-sm p-6" data-testid="claim-account-section">
      <div className="text-center mb-6">
        <div className={`w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-3 ${isManual ? 'bg-[#FFB800]/10' : 'bg-emerald-500/10'}`}>
          <CheckCircle className={`w-8 h-8 ${isManual ? 'text-[#FFB800]' : 'text-emerald-400'}`} />
        </div>
        <h3 className="text-xl font-bold text-[#EAE7E1]">{isManual ? 'Request Submitted' : 'Pass Secured!'}</h3>
        <p className="text-sm text-[#777] mt-1">Saved to {purchaseEmail}</p>
      </div>

      <div className="border border-[#1A1A1A] rounded-sm p-4 mb-4 bg-[#050505]">
        <p className="text-sm text-[#EAE7E1] font-semibold mb-1">Create a free VelvetGate account</p>
        <p className="text-xs text-[#AFAFAF] mb-3">Lets you sign in from any device to view, download, or add your pass to Apple/Google Wallet — anytime.</p>
        <div className="space-y-3">
          <div>
            <Label>Display Name <span className="text-[#555] font-normal">(optional)</span></Label>
            <Input value={claimForm.screen_name} onChange={e => setClaimForm({ ...claimForm, screen_name: e.target.value })} placeholder="How you want to be known" className="mt-1" data-testid="claim-screen-name" />
          </div>
          <div>
            <Label>Password *</Label>
            <Input type="password" value={claimForm.password} onChange={e => setClaimForm({ ...claimForm, password: e.target.value })} placeholder="At least 6 characters" minLength={6} className="mt-1" data-testid="claim-password" />
          </div>
          <Button onClick={onClaim} disabled={claiming} className="w-full bg-[#D4AF37] hover:bg-[#F2D27A] text-[#050505] rounded-sm font-bold" data-testid="claim-account-button">
            {claiming ? 'Creating...' : 'Create Account'}
          </Button>
        </div>
      </div>

      <button onClick={() => navigate(`/ticket/${ticketId}`)} className="w-full text-center text-sm text-[#777] hover:text-[#AFAFAF] transition-colors" data-testid="skip-claim-button">
        Skip — view my pass
      </button>
      <p className="text-[11px] text-[#555] text-center mt-2" data-testid="skip-claim-hint">
        Your pass is saved to <span className="text-[#AFAFAF]">{purchaseEmail}</span>. Sign in later with this email to find it in My Passes.
      </p>
    </div>
  );
}
